import useFetchEntities from 'hooks/useFetchEntities';
import useFetchEntity from 'hooks/useFetchEntity';
import { formatDateToShortFormat, formatTimeStringToHHMM } from 'utils/IGdateTimeFunctions';
import { sortDateAndTime } from 'utils/sorters';
import { useEffect, useRef, useState } from 'react';
import { Select } from 'antd';
import usePersistentStore from 'store/persistentStore';

function GameSelector({ value, onChange, persistentStoreKey = 'generic-gameselector' }) {

    const { data: competitions, isLoading } = useFetchEntities('Competition', {
        relations: [
            'Seasons(seasonId,name,startDate)',
            'Seasons>Groups(groupId,name)',
            'Seasons>Groups>Phases(phaseId,name,startDate)',
        ].join(','),
    });

    const { data: initialGame } = useFetchEntity(value ? 'Game' : null, value, 'Phase>Group>Season>Competition');

    const setSelectedCompetition = usePersistentStore((state) => state.set(`${persistentStoreKey}-competition`));
    const setSelectedSeason = usePersistentStore((state) => state.set(`${persistentStoreKey}-season`));
    const setSelectedGroup = usePersistentStore((state) => state.set(`${persistentStoreKey}-group`));
    const setSelectedPhase = usePersistentStore((state) => state.set(`${persistentStoreKey}-phase`));

    const persistentCompetition = usePersistentStore((state) => state.get(`${persistentStoreKey}-competition`));
    const persistentSeason = usePersistentStore((state) => state.get(`${persistentStoreKey}-season`));
    const persistentGroup = usePersistentStore((state) => state.get(`${persistentStoreKey}-group`));
    const persistentPhase = usePersistentStore((state) => state.get(`${persistentStoreKey}-phase`));


    // After initialization, use persistent store values (which are set from game value if provided)
    // Before initialization, use persistent store as fallback
    const selectedCompetition = persistentCompetition;
    const selectedSeason = persistentSeason;
    const selectedGroup = persistentGroup;
    const selectedPhase = persistentPhase;

    const { data: games, isLoading: gamesLoading } = useFetchEntities(selectedPhase && 'Game', {
        filters: {
            phaseId: selectedPhase,
        },
        relations: [
            'Venue(name)',
            'HomeTeam(short)',
            'AwayTeam(short)',
        ].join(','),
        onlyAttributes: [
            'gameId',
            'startDate',
            'startTime',
        ].join(','),
    });

    const [seasonOpen, setSeasonOpen] = useState(false);
    const [groupOpen, setGroupOpen] = useState(false);
    const [phaseOpen, setPhaseOpen] = useState(false);
    const [gameOpen, setGameOpen] = useState(false);
    const [shouldOpenGame, setShouldOpenGame] = useState(false);

    const seasonRef = useRef(null);
    const groupRef = useRef(null);
    const phaseRef = useRef(null);
    const gameRef = useRef(null);

    const onSeasonDropdownVisibleChange = (open) => {
        setSeasonOpen(open);
    }

    const onGroupDropdownVisibleChange = (open) => {
        setGroupOpen(open);
    }

    const onPhaseDropdownVisibleChange = (open) => {
        setPhaseOpen(open);
    }

    const handleCompetitionChange = (competitionId) => {
        setSelectedCompetition(competitionId);
        setSelectedSeason(null);
        setSelectedGroup(null);
        setSelectedPhase(null);
        onChange && onChange(null);
        if (!competitionId) {
            setSeasonOpen(false);
            setGroupOpen(false);
            setPhaseOpen(false);
            setGameOpen(false);
        }
    }

    const handleCompetitionSelect = () => {
        setSeasonOpen(true);
        setGroupOpen(false);
        setPhaseOpen(false);
        setGameOpen(false);
        setTimeout(() => { seasonRef.current?.focus?.(); }, 0);
    }

    const handleSeasonChange = (seasonId) => {
        setSelectedSeason(seasonId);
        setSelectedGroup(null);
        setSelectedPhase(null);
        onChange && onChange(null);
    }

    const handleSeasonSelect = () => {
        setSeasonOpen(false);
        setGroupOpen(true);
        setPhaseOpen(false);
        setGameOpen(false);
        setTimeout(() => { groupRef.current?.focus?.(); }, 0);
    }

    const handleGroupChange = (groupId) => {
        setSelectedGroup(groupId);
        setSelectedPhase(null);
        onChange && onChange(null);
    }

    const handleGroupSelect = () => {
        setSeasonOpen(false);
        setGroupOpen(false);
        setPhaseOpen(true);
        setGameOpen(false);
        setTimeout(() => { phaseRef.current?.focus?.(); }, 0);
    }

    const handlePhaseChange = (phaseId) => {
        setSelectedPhase(phaseId);
        onChange && onChange(null);
    }

    const handlePhaseSelect = () => {
        setSeasonOpen(false);
        setGroupOpen(false);
        setPhaseOpen(false);
        setShouldOpenGame(true);
    }

    const handleGameChange = (gameId) => {
        if (onChange) {
            onChange(gameId);
        }
        setGameOpen(false);
        setShouldOpenGame(false);
    }

    useEffect(() => {
        console.log('GameSelector - useEffect:', 'competitions:', competitions != null, 'initialGame:', initialGame != null);
        if (competitions && initialGame) {
            if (value && initialGame?.phaseId) {
                const phaseId = initialGame.phaseId;
                // Find the phase in the competitions structure to get the full hierarchy
                for (const competition of competitions) {
                    for (const season of competition.Seasons || []) {
                        for (const group of season.Groups || []) {
                            const phase = group.Phases?.find(p => p.phaseId === phaseId);
                            if (phase) {
                                setSelectedCompetition(competition.competitionId);
                                setSelectedSeason(season.seasonId);
                                setSelectedGroup(group.groupId);
                                setSelectedPhase(phaseId);
                                console.log('GameSelector - useEffect:', 'Setting values:', competition.name, season.name, group.name, phase.name);
                                return;
                            }
                        }
                    }
                }
            }
        }
    }, [initialGame, competitions]);

    useEffect(() => {
        if (shouldOpenGame && !gamesLoading) {
            setGameOpen(true);
            setShouldOpenGame(false);
        }
    }, [shouldOpenGame, gamesLoading]);

    useEffect(() => { if (seasonOpen) { seasonRef.current?.focus?.(); } }, [seasonOpen]);
    useEffect(() => { if (groupOpen) { groupRef.current?.focus?.(); } }, [groupOpen]);
    useEffect(() => { if (phaseOpen) { phaseRef.current?.focus?.(); } }, [phaseOpen]);
    useEffect(() => { if (gameOpen) { gameRef.current?.focus?.(); } }, [gameOpen]);

    return <div className="flex gap-2 w-full">
        <Select
            className="flex-1"
            showSearch
            dropdownMatchSelectWidth={false}
            allowClear
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            disabled={isLoading}
            placeholder={isLoading ? 'Loading...' : 'Competition'}
            options={competitions?.slice()?.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))?.map(c => ({ value: c.competitionId, label: c.name }))}
            value={isLoading ? null : selectedCompetition}
            onChange={handleCompetitionChange}
            open={undefined}
            onSelect={handleCompetitionSelect}
        />
        <Select
            ref={seasonRef}
            className="flex-1"
            showSearch
            dropdownMatchSelectWidth={false}
            allowClear
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            disabled={isLoading || !selectedCompetition}
            placeholder={isLoading ? 'Loading...' : 'Season'}
            options={competitions?.find(c => c.competitionId == selectedCompetition)?.Seasons?.slice()?.sort((a, b) => sortDateAndTime(a.startDate, '00:00', b.startDate, '00:00'))?.reverse()?.map(s => ({ value: s.seasonId, label: s.name }))}
            value={isLoading ? null : selectedSeason}
            onChange={handleSeasonChange}
            open={seasonOpen}
            onDropdownVisibleChange={onSeasonDropdownVisibleChange}
            onSelect={handleSeasonSelect}
        />
        <Select
            ref={groupRef}
            className="flex-1"
            showSearch
            dropdownMatchSelectWidth={false}
            allowClear
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            disabled={isLoading || !selectedSeason}
            placeholder={isLoading ? 'Loading...' : 'Group'}
            options={competitions?.find(c => c.competitionId == selectedCompetition)?.Seasons?.find(s => s.seasonId == selectedSeason)?.Groups?.slice()?.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))?.map(g => ({ value: g.groupId, label: g.name }))}
            value={isLoading ? null : selectedGroup}
            onChange={handleGroupChange}
            open={groupOpen}
            onDropdownVisibleChange={onGroupDropdownVisibleChange}
            onSelect={handleGroupSelect}
        />
        <Select
            ref={phaseRef}
            className="flex-1"
            showSearch
            dropdownMatchSelectWidth={false}
            allowClear
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            disabled={isLoading || !selectedGroup}
            placeholder={isLoading ? 'Loading...' : 'Phase'}
            options={competitions?.find(c => c.competitionId == selectedCompetition)?.Seasons?.find(s => s.seasonId == selectedSeason)?.Groups?.find(g => g.groupId == selectedGroup)?.Phases?.slice()?.sort((a, b) => sortDateAndTime(a.startDate, '00:00', b.startDate, '00:00'))?.map(p => ({ value: p.phaseId, label: p.name }))}
            value={isLoading ? null : selectedPhase}
            onChange={handlePhaseChange}
            open={phaseOpen}
            onDropdownVisibleChange={onPhaseDropdownVisibleChange}
            onSelect={handlePhaseSelect}
        />
        <Select
            ref={gameRef}
            className="flex-1"
            showSearch
            dropdownMatchSelectWidth={false}
            allowClear
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            disabled={!selectedPhase}
            placeholder={gamesLoading ? 'Loading...' : 'Game'}
            options={games?.slice()?.sort((g1, g2) => sortDateAndTime(g1.startDate, g1.startTime, g2.startDate, g2.startTime))?.map(gm => ({ value: gm.gameId, label: `${formatDateToShortFormat(gm.startDate)} ${formatTimeStringToHHMM(gm.startTime)} - ${gm.Venue?.name} - ${gm.HomeTeam?.short} vs ${gm.AwayTeam?.short}` }))}
            value={gamesLoading ? null : value}
            onChange={handleGameChange}
            open={gameOpen}
            onDropdownVisibleChange={setGameOpen}
            notFoundContent={gamesLoading ? 'Loading...' : undefined}
        />

    </div>
}

export default GameSelector